import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip,
  Icon
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import "survey-core/survey-core.css";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import Button from "components/CustomButtons/Button.js";
import { getFormEntriesAPI, deleteFormEntryAPI, exportFormEntryToPDF } from "views/APIs/APIUtility";
import NotificationContext from "views/Components/Context.js";
import { CloudDownload } from "@material-ui/icons";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

const FormEntries = ({
  formID,
  published_form_link,              // async (id) => void
  onUnpublishForm,            // async () => void
}) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // store entry to delete
  const [busy, setBusy] = useState(false);
  const { showNotification } = React.useContext(NotificationContext);


  const [formDataEntries, setFormDataEntries] = useState([]);
  const handleView = (entry) => {
    setSelectedEntry(entry);
    setViewerOpen(true);
  };

  const viewerModel = useMemo(() => {
    if (!viewerOpen || !selectedEntry?.survey_json) return null;
    try {
      const m = new Model(selectedEntry.survey_json); // schema for this entry
      m.data = selectedEntry.form_data || {};         // <-- preload answers
      m.mode = "display";                             // read-only (use "edit" to allow edits)
      return m;
    } catch (e) {
      console.error("Survey model error:", e);
      return null;
    }
  }, [viewerOpen, selectedEntry]);

  useEffect(() => {
    async function fetchFormEntries() {
      try {
        const formData = new FormData();
        formData.append("json_project_id", formID);
        const response = await getFormEntriesAPI(formData);
        setFormDataEntries(response);

      } catch (error) {
        console.error(error);
      }
    }
    fetchFormEntries();
  }, [formID]);

  const handleDelete = async () => {
    try {
      setBusy(true);
      const formData = new FormData();
      formData.append("json_project_id", formID);
      formData.append("form_response_id", confirmDelete._id);
      await deleteFormEntryAPI(formData);
      setFormDataEntries(formDataEntries.filter((e) => e._id !== confirmDelete._id));
      showNotification("Entry deleted successfully", "success");
      setConfirmDelete(null);
    } catch (e) {
      console.error(e);
      showNotification(e?.message || "Delete failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleUnpublish = async () => {
    try {
      setBusy(true);
      if (onUnpublishForm) await onUnpublishForm();
      showNotification("Form unpublished successfully", "success");
    } catch (e) {
      console.error(e);
      showNotification(e?.message || "Unpublish failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(published_form_link);
    showNotification("Link copied to clipboard.", "success");
  };

  const handleExportToPDF = async (entry) => {
    try {
      setBusy(true);
      const formData = new FormData();
      formData.append("json_project_id", formID);
      formData.append("form_response_id", entry._id);
      const response = await exportFormEntryToPDF(formData);
      // download the pdf
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `form_entry_${entry._id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showNotification("Entry exported to PDF successfully", "success");
    } catch (e) {
      console.error(e);
      showNotification(e?.message || "Export failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5">Form Entries</Typography>
          <Typography variant="body1">View and manage form entries for the published form.</Typography>
        </Box>

        <Box>

          <Tooltip
            title={published_form_link ? "" : "Publish the form on the previous step to get a link."}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="quSandBoxColor"
                onClick={handleCopyLink}
                disabled={!published_form_link}
              >
                Copy Form Link
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={published_form_link ? "" : "Publish the form on the previous step to unpublish it."}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="danger"
                onClick={handleUnpublish}
                disabled={busy || !published_form_link}
              >
                Unpublish form
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Divider style={{ margin: "16px 0" }} />


      {/* Entries Table */}
      {(!formDataEntries || formDataEntries.length === 0) ? (
        <Typography variant="body2" color="textSecondary">
          No entries yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Registered at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(formDataEntries || []).map((e) => (
              <TableRow
                key={e._id}
                hover
                style={{ cursor: "pointer" }}
                onClick={() => handleView(e)}
              >
                <TableCell>{e._id}</TableCell>
                <TableCell>{fmt(e.timestamp)}</TableCell>
                <TableCell align="right" onClick={(ev) => ev.stopPropagation()}>
                  {/* <Tooltip title="Export to PDF">
                    <IconButton size="small" onClick={() => handleExportToPDF(e)}>
                      <CloudDownload />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleView(e)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete(e)}
                      disabled={busy}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Viewer Modal */}
      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Entry: {selectedEntry?._id}</DialogTitle>
        <DialogContent dividers>
          {viewerModel ? (
            <Survey model={viewerModel} />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Unable to render this entry (missing or invalid survey JSON).
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewerOpen(false)} color="secondary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !busy && setConfirmDelete(null)}
      >
        <DialogTitle>Delete this entry?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            This cannot be undone. Entry ID: <b>{confirmDelete?._id}</b>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="danger"
            variant="contained"
            disabled={busy}
          >
            {busy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};


export default FormEntries;
